import {
  Alert,
  AlertType,
  Checkbox,
  Text,
  TextSize,
  TextWeight,
  makeStyles
} from '@focaldata/cin-ui-components'
import { useRef } from 'react'
import LaunchErrorDetails from '../../../../controls/LaunchErrorDetails'
import { getLaunchErrorDetails } from '../../../../utils/launchErrorDetails'
import { KpiChanges, WaveKpiChangesState } from './useWaveKpiChanges'

type KpiChangeSubject = KpiChanges['subjects'][number]

const useStyles = makeStyles()({
  section: { marginBottom: 16 },
  region: {
    maxHeight: 240,
    overflowY: 'auto',
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  changes: { margin: '4px 0 0', paddingLeft: 20 },
  linkButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    cursor: 'pointer',
    color: 'var(--primary)',
    fontWeight: 600
  }
})

const waveName = (wave: { number: number; label?: string | null }) =>
  wave.label || `Wave ${wave.number}`

const Subject = ({ subject }: { subject: KpiChangeSubject }) => {
  const { classes } = useStyles()
  const title = subject.label
    ? `${subject.questionNumber} ${subject.label}`
    : subject.questionNumber
  return (
    <div role="group" aria-label={title}>
      <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
        {title}
      </Text>
      <ul className={classes.changes} aria-label="Changes">
        {subject.changes.map((change, index) => (
          <li key={index}>
            <Text size={TextSize.ms}>
              {change.needsAcknowledgement
                ? change.description
                : `For information: ${change.description}`}
            </Text>
          </li>
        ))}
      </ul>
    </div>
  )
}

export const WaveKpiChanges = ({
  status,
  kpiChanges,
  error,
  requiresAcknowledgement,
  acknowledged,
  setAcknowledged,
  launchWithoutChecking,
  setLaunchWithoutChecking,
  listUpdated,
  tryAgain,
  rechecking
}: WaveKpiChangesState) => {
  const { classes } = useStyles()
  const sectionRef = useRef<HTMLDivElement>(null)

  const updatedAlert = listUpdated && (
    <Alert severity={AlertType.WARNING} style={{ marginBottom: 8 }}>
      The KPI changes have been updated since this was opened.{' '}
      {requiresAcknowledgement
        ? 'Check them again before launching.'
        : 'None of them need checking now.'}
    </Alert>
  )

  const renderContent = () => {
    if (status === 'loading') {
      return (
        <Text size={TextSize.ms}>
          Checking for KPI changes since the previous wave…
        </Text>
      )
    }

    if (status === 'error') {
      const details = getLaunchErrorDetails(error)
      return (
        <>
          <Alert severity={AlertType.WARNING} style={{ marginBottom: 8 }}>
            Something went wrong checking the KPI changes since the previous
            wave.{' '}
            <button
              type="button"
              className={classes.linkButton}
              onClick={() => {
                // Keep focus in the section once this button unmounts.
                sectionRef.current?.focus()
                void tryAgain()
              }}
            >
              Try again
            </button>
            {details && <LaunchErrorDetails details={details} />}
          </Alert>
          <Checkbox
            label="Launch without checking KPI changes"
            checked={launchWithoutChecking}
            onChange={(event) => setLaunchWithoutChecking(event.target.checked)}
          />
        </>
      )
    }

    if (status === 'none' || !kpiChanges?.previousWave) return updatedAlert

    const heading = `KPI changes since ${waveName(kpiChanges.previousWave)}`
    return (
      <>
        {updatedAlert}
        <Text size={TextSize.m} weight={TextWeight.SemiBold}>
          {heading}
        </Text>
        <div
          className={classes.region}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scrollable region needs a tab stop to be operable by keyboard (WCAG 2.1.1)
          tabIndex={0}
          role="region"
          aria-label={heading}
        >
          {kpiChanges.subjects.map((subject) => (
            <Subject key={subject.trackingId} subject={subject} />
          ))}
        </div>
        {requiresAcknowledgement && (
          <Checkbox
            label={`I've checked these KPI changes and understand they may affect comparisons with ${waveName(
              kpiChanges.previousWave
            )}`}
            checked={acknowledged}
            disabled={rechecking}
            onChange={(event) => setAcknowledged(event.target.checked)}
          />
        )}
      </>
    )
  }

  const content = renderContent()
  if (!content) return null

  return (
    <div ref={sectionRef} className={classes.section} tabIndex={-1}>
      {content}
    </div>
  )
}
